import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { toast } from "@shared/hooks/use-toast";
import { templatesApi } from "./api";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { queryClient } from "@shared/api/queries/queryClient";

const TEMPLATE_LIST_KEY = ["ux", "templates", "templatesList"];
const TEMPLATE_DATA_KEY = QueryKeyFactory.module.ux.templates.templateData().slice(0, 1);

export const useCreateTemplate = () => {
  return useSmartMutation(
    (payload: any) => templatesApi.createTemplate(payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Template created successfully",
        });
        queryClient.refetchQueries({ queryKey: TEMPLATE_LIST_KEY });
      },
    }
  );
};

export const useTestSendTemplate = () => {
  return useSmartMutation(
    (payload: any) => templatesApi.testSendTemplate(payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Test message sent successfully",
        });
      },
    }
  );
};

export const useUpdateTemplate = (templateId) => {
  return useSmartMutation(
    (payload: any) => templatesApi.updateTemplate(payload, templateId),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Template updated successfully",
        });
        queryClient.refetchQueries({ queryKey: TEMPLATE_LIST_KEY });
        queryClient.refetchQueries({ queryKey: TEMPLATE_DATA_KEY });
      },
    }
  );
};

export const useUpdateTemplateStatus = (templateId) => {
  return useSmartMutation(
    (payload: any) => templatesApi.updateTemplateStatus(payload, templateId),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Template status updated",
        });
        queryClient.refetchQueries({ queryKey: TEMPLATE_LIST_KEY });
        queryClient.refetchQueries({ queryKey: TEMPLATE_DATA_KEY });
      },
    }
  );
};

export const useCloneTemplate = (templateId) => {
  return useSmartMutation(
    (payload: any) => templatesApi.cloneTemplate(payload, templateId),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Template cloned successfully",
        });
        queryClient.refetchQueries({ queryKey: TEMPLATE_LIST_KEY });
      },
    }
  );
};

export const useBulkAddTemplate = () => {
  return useSmartMutation(
    (payload: any) => templatesApi.bulkAddTemplate(payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Templates uploaded successfully",
        });
      },
    }
  );
};

// --------------------- QUERIES -----------------------------

export const useTemplateList = ( 
  page?: number,
  searchValue?: string,
  channel?: string,
  audienceType?: string,
  status?: string
) => {
  return useSmartQuery(
    QueryKeyFactory.module.ux.templates.templatesList(
      page,
      searchValue,
      channel,
      audienceType,
      status
    ),
    () =>
      templatesApi.getTemplateList({
        page,
        search: searchValue,
        channel,
        audienceType,
        status,
      })
  );
};

export const useDefaultTemplate = () => {
  return useSmartQuery(
    QueryKeyFactory.module.ux.templates.defaultTemplate(),
    () => templatesApi.getDefaultTemplate()
  );
};

export const useGetTemplateData = (templateId: any) => {
  return useSmartQuery(
    QueryKeyFactory.module.ux.templates.templateData(templateId),
    () => templatesApi.getTemplateData(templateId),
    {
      enabled: !!templateId,
    }
  );
};

export const useVersionHistory = (templateId: any) => {
  return useSmartQuery(
    QueryKeyFactory.module.ux.templates.versionHistory(templateId),
    () => templatesApi.getVersionHistory(templateId),
    {
      enabled: !!templateId,
    }
  );
};
export const useTemplateWorkflow = (templateId: any) => {
  return useSmartQuery(
    QueryKeyFactory.module.ux.templates.templateWorkflow(templateId),
    () => templatesApi.getTemplateWorkflow(templateId),
    {
      enabled: !!templateId,
    }
  );
};
export const useGetTemplateMetaData = () => {
  return useSmartQuery(
    QueryKeyFactory.module.ux.templates.templateMetaData(),
    () => templatesApi.getTemplateMetaData()
  );
};

export const useSegmentData = () => {
  return useSmartQuery(
    QueryKeyFactory.module.ux.templates.segmentData(),
    () => templatesApi.getSegmentsData()
  );
};

export const useSignatureData = () => {
  return useSmartQuery(
    QueryKeyFactory.module.ux.templates.signatureData(),
    () => templatesApi.getSignatureData()
  );
};
